import {Box, Text} from '@dagster-io/ui-components';
import {useSearchParams} from 'next/navigation';
import {useReCaptcha} from 'next-recaptcha-v3';
import {useEffect, useRef, useState} from 'react';

import CenteredButton from '@/components/CenteredButton';
import {AuthProvider, AuthProviderIcons} from '@/utils/AuthProvider';

export const labels: Record<AuthProvider, string> = {
  google: 'Sign up with Google',
  github: 'Sign up with GitHub',
  sso: '', // Not used
};

interface Props {
  provider: AuthProvider;
}

export default function SignUpWithProvider({provider}: Props) {
  const params = useSearchParams();
  const next = params.get('next');
  const token = params.get('token') || '';

  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formRef = useRef<HTMLFormElement>(null);

  const {executeRecaptcha} = useReCaptcha();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);

    // Get the Recaptcha token and set it in state so that it is included in
    // the rendered form, which is then submitted programmatically in the
    // subsequent effect.
    try {
      const token = await executeRecaptcha('signup');
      setRecaptchaToken(token);
    } catch {
      setError('An error occurred while signing up with this provider.');
      setSubmitting(false);
    }
  };

  useEffect(() => {
    // Programmatically submit the form once we have rendered a Recaptcha token in the form.
    // The form uses a GET request to direct the user to the correct destination.
    if (recaptchaToken) {
      requestAnimationFrame(() => {
        formRef.current?.submit();
      });
    }
  }, [recaptchaToken]);

  return (
    <Box flex={{direction: 'column', gap: 8, alignItems: 'stretch'}}>
      <form
        method="GET"
        action={`/signup/${provider}`}
        onSubmit={handleSubmit}
        ref={formRef}
        data-testid={`${provider}-signup-form`}
        style={{display: 'flex', flexDirection: 'column'}}
      >
        <input type="hidden" name="token" value={token} />
        <input type="hidden" name="recaptcha_token" value={recaptchaToken || ''} />
        {next ? <input type="hidden" name="next" value={next} /> : null}
        <CenteredButton
          id={`submit-${provider}`}
          disabled={submitting}
          icon={AuthProviderIcons[provider]}
          label={submitting ? 'Signing up…' : labels[provider]}
          style={{width: '100%'}}
        />
      </form>
      {error && !submitting ? (
        <Text size={12} color="textRed" style={{textAlign: 'center'}}>
          {error}
        </Text>
      ) : null}
    </Box>
  );
}
